const statusMap = {
  in_attesa: "Presa in Carico",
  ritirato: "Ritirato",
  in_transito: "In Transito",
  in_consegna: "In Consegna",
  consegnato: "Consegnato",
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return "";
  const [year, month] = monthKey.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
};

const formatAddressBlock = (details, fallback) => {
  const firstLine = details?.name || details?.company || fallback || "-";
  const secondLine = details?.address || "-";
  const thirdLine = [details?.cap, details?.city || fallback, details?.province ? `(${details.province})` : ""]
    .filter(Boolean)
    .join(" ");

  return [firstLine, secondLine, thirdLine || fallback || "-"].filter(Boolean).join("<br />");
};

const getColliSummary = (item) => {
  if (item.colli?.length) {
    const totalQty = item.colli.reduce((acc, c) => acc + (Number(c.quantita) || 1), 0);
    const lines = item.colli.map((c) => {
      const qty = Number(c.quantita) || 1;
      const tipo = c.tipo === "pallet" ? "PLT" : c.tipo === "mezzo_bancale" ? "1/2PLT" : "CLO";
      const label = qty > 1 ? `${tipo}x${qty}` : tipo;
      const peso = c.peso ? `${c.peso} kg` : "";
      const dims = c.dimensioni || "";
      return [label, peso, dims].filter(Boolean).join(" ");
    });
    const header = `<strong>${totalQty} ${totalQty === 1 ? "collo" : "colli"}</strong>`;
    return `${header}<br /><span style="font-size:11px;color:#555;">${lines.join("<br />")}</span>`;
  }
  if (item.numero_colli) return `${item.numero_colli} ${item.tipo_imballo || "colli"}`;
  return "-";
};

const getMerceDetail = (item) => {
  const desc = (item.descrizione_merce || item.note_merce || "").trim();
  return desc ? `<strong>${desc}</strong>` : "-";
};

export const printMonthlySummary = ({ items = [], mode = "spedizione", monthKey = "", user = null }) => {
  if (!items.length) return;

  const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e85a7b3-7844-4782-8600-31defd3a9a43/artifacts/5fhyey8q_MV%20trasporti%20logo.png";
  const title = mode === "ritiro" ? "Resoconto mensile ritiri" : "Resoconto mensile spedizioni";
  const rows = items.map((item) => `
    <tr>
      <td><div><strong>${item.tracking_number}</strong></div><div style="font-size:11px;color:#555;margin-top:2px;">${new Date(item.created_at).toLocaleDateString("it-IT")}</div></td>
      <td>${statusMap[item.status] || item.status || "-"}</td>
      <td>${item.service_type || "-"}</td>
      <td>${getColliSummary(item)}</td>
      <td>${getMerceDetail(item)}</td>
      <td>${formatAddressBlock(item.origin_details, item.origin)}</td>
      <td>${formatAddressBlock(item.dest_details, item.destination)}</td>
      <td>${item.note || item.ddt_reference || "-"}</td>
    </tr>
  `).join("");

  const html = `<!DOCTYPE html>
  <html lang="it">
    <head>
      <meta charset="UTF-8" />
      <title>${title}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 24px; color: #111; }
        .header { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 24px; border-bottom: 2px solid #111; padding-bottom: 16px; }
        .eyebrow { color: #FF5200; font-size: 12px; font-weight: 900; letter-spacing: 0.16em; text-transform: uppercase; }
        h1 { margin: 6px 0; font-size: 28px; text-transform: uppercase; }
        .meta { font-size: 13px; color: #555; line-height: 1.6; }
        .summary { display: flex; gap: 12px; margin-bottom: 18px; }
        .chip { border: 1px solid #ddd; padding: 10px 12px; font-size: 12px; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        th, td { border: 1px solid #ddd; padding: 8px 6px; vertical-align: top; font-size: 11.5px; line-height: 1.4; word-wrap: break-word; }
        th { background: #f5f5f5; text-align: left; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; }
        @media print { body { margin: 12mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { size: landscape; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div style="display:flex;align-items:center;gap:16px;">
          <img src="${LOGO_URL}" alt="MV Trasporti" style="height:40px;width:auto;" />
          <div>
            <h1>${title}</h1>
            <div class="meta">Mese: <strong>${formatMonthLabel(monthKey)}</strong></div>
          </div>
        </div>
        <div class="meta">
          ${user?.company ? `<div><strong>Azienda:</strong> ${user.company}</div>` : ""}
          ${user?.name ? `<div><strong>Cliente:</strong> ${user.name}</div>` : ""}
          ${user?.email ? `<div><strong>Email:</strong> ${user.email}</div>` : ""}
        </div>
      </div>
      <div class="summary">
        <div class="chip">Totale righe: ${items.length}</div>
        <div class="chip">Tipo: ${mode === "ritiro" ? "Ritiri" : "Spedizioni"}</div>
      </div>
      <table>
        <colgroup>
          <col style="width:8%;" />
          <col style="width:7%;" />
          <col style="width:7%;" />
          <col style="width:10%;" />
          <col style="width:13%;" />
          <col style="width:20%;" />
          <col style="width:20%;" />
          <col style="width:15%;" />
        </colgroup>
        <thead>
          <tr>
            <th>Tracking / Data</th>
            <th>Stato</th>
            <th>Servizio</th>
            <th>Colli</th>
            <th>Merce</th>
            <th>Origine</th>
            <th>Destinazione</th>
            <th>Note / Riferimento</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=function(){window.print();}<\/script>
    </body>
  </html>`;

  const win = window.open("", "_blank", "width=1200,height=900");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
};